import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Notification, NotificationFilter } from 'modules/core/domain/interfaces/supabase/notifications/types';
import {
  getGlobalLastReadNotificationId,
  getLastReadNotificationId,
  setGlobalLastReadNotificationId,
  setLastReadNotificationId,
} from '../domain/utils/notifications';
import { createNotification, deleteNotification, fetchNotificationById, fetchNotifications, updateNotification } from './actions';

interface NotificationsState {
  items: Notification[];
  total: number;
  loading: boolean;
  error: string | null;
  currentNotification: Notification | null;
  filters: NotificationFilter;
  pageSize: number;
  hasMore: boolean;
  // Единая система хранения отметок прочитанного
  globalLastReadId: number | null;
  pageLastReadId: number | null;
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  currentNotification: null,
  filters: {
    type: 'all',
    sort: 'desc',
    limit: 10,
    offset: 0,
  },
  pageSize: 10,
  hasMore: true,
  globalLastReadId: getGlobalLastReadNotificationId(),
  pageLastReadId: getLastReadNotificationId(),
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
    setFilters: (state, action: PayloadAction<NotificationFilter>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Сбрасываем offset при изменении фильтров
      };
      state.items = []; // Очищаем список при изменении фильтров
      state.hasMore = true; // Сбрасываем hasMore при смене фильтров
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.filters.offset = action.payload;
    },
    // Единая система: управление отметками прочитанного
    setGlobalLastReadId: (state, action: PayloadAction<number>) => {
      state.globalLastReadId = state.globalLastReadId == null ? action.payload : Math.max(state.globalLastReadId, action.payload);
      setGlobalLastReadNotificationId(action.payload);
    },
    setPageLastReadId: (state, action: PayloadAction<number>) => {
      state.pageLastReadId = action.payload;
      setLastReadNotificationId(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        let newItems: Notification[];
        if (state.filters.offset === 0) {
          state.items = action.payload.notifications;
        } else {
          // Добавляем только уникальные по id
          const existingIds = new Set(state.items.map((n) => n.id));
          newItems = action.payload.notifications.filter((n) => !existingIds.has(n.id));
          state.items = [...state.items, ...newItems];
        }
        state.total = action.payload.total;
        // Принудительная сортировка по дате
        state.items.sort((a, b) => {
          const aDate = new Date(a.createdAt).getTime();
          const bDate = new Date(b.createdAt).getTime();
          return state.filters.sort === 'asc' ? aDate - bDate : bDate - aDate;
        });
        // Контроль hasMore
        const pageSize = state.filters.limit || state.pageSize;
        state.hasMore = action.payload.notifications.length >= pageSize;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        // Обработка ошибки PGRST103 (Requested range not satisfiable)
        if (action.error && (action.error as any).code === 'PGRST103') {
          // Просто игнорируем, не выставляем ошибку
          return;
        }
        state.error = action.error.message ?? '';
      })

      // Fetch single notification
      .addCase(fetchNotificationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationById.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        state.currentNotification = action.payload;
      })
      .addCase(fetchNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '';
      })

      // Create notification
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '';
      })

      // Update notification
      .addCase(updateNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentNotification?.id === action.payload.id) {
          state.currentNotification = action.payload;
        }
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '';
      })

      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total -= 1;
        if (state.currentNotification?.id === action.payload) {
          state.currentNotification = null;
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? '';
      });
  },
});

export const { clearError, clearCurrentNotification, setFilters, setOffset, setGlobalLastReadId, setPageLastReadId } =
  notificationsSlice.actions;
export default notificationsSlice.reducer;
